import { useEffect, useRef, useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { requestEmailLogin, verifyEmailLogin } from '@client/api/auth';
import { ApiError } from '@client/api/client';
import { getDeviceId } from '@client/lib/device-id';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BrandMark } from '@ui/components/BrandMark';
import { Button } from '@ui/components/Button';
import { Field } from '@ui/components/Field';
import { Spinner } from '@ui/components/Spinner';
import { JewelryBackdrop } from './JewelryBackdrop';

type Step = 'email' | 'code';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  // AuthGate stashes the original deep-linked URL on `location.state.from` when
  // it bounces an unauthenticated user here. Honor it so post-login lands back
  // on the intended page instead of always dumping to '/'.
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';
  // Owner-issued one-shot magic links land at /login?email=&otp= and skip both
  // form steps. We honor exactly once per page-load to avoid loops on bad codes.
  const autoEmail = params.get('email')?.trim().toLowerCase() ?? '';
  const autoOtp = params.get('otp')?.trim() ?? '';
  const autoMode = autoEmail.length > 0 && /^\d{6}$/.test(autoOtp);
  const [step, setStep] = useState<Step>(autoMode ? 'code' : 'email');
  const [email, setEmail] = useState(autoEmail);
  const [fullName, setFullName] = useState('');
  const [code, setCode] = useState(autoMode ? autoOtp : '');
  // Surface OAuth bounce-back errors. /api/auth/google/callback redirects to
  // /login?auth_error=... when Google cancels or token exchange fails.
  const oauthError = params.get('auth_error');
  const [error, setError] = useState<string | null>(
    oauthError ? formatOauthError(oauthError) : null,
  );

  const requestMutation = useMutation({
    mutationFn: (input: { email: string; fullName?: string }) => requestEmailLogin(input),
    onSuccess: () => {
      setError(null);
      setStep('code');
    },
    onError: (err: unknown) => setError(formatError(err)),
  });

  const verifyMutation = useMutation({
    mutationFn: (input: { email: string; code: string; deviceId: string }) =>
      verifyEmailLogin(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate(from, { replace: true });
    },
    onError: (err: unknown) => setError(formatError(err)),
  });

  const onSubmitEmail = (e: FormEvent): void => {
    e.preventDefault();
    setError(null);
    const trimmedName = fullName.trim();
    requestMutation.mutate({
      email: email.trim().toLowerCase(),
      ...(trimmedName ? { fullName: trimmedName } : {}),
    });
  };

  const onSubmitCode = (e: FormEvent): void => {
    e.preventDefault();
    setError(null);
    verifyMutation.mutate({
      email: email.trim().toLowerCase(),
      code: code.trim(),
      deviceId: getDeviceId(),
    });
  };

  // Auto-fire verify when arriving via owner-issued magic link.
  const autoFiredRef = useRef(false);
  useEffect(() => {
    if (!autoMode || autoFiredRef.current) return;
    autoFiredRef.current = true;
    verifyMutation.mutate({ email: autoEmail, code: autoOtp, deviceId: getDeviceId() });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot effect on first mount
  }, []);

  return (
    <main
      id="main"
      className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-gradient-to-br from-amber-50 via-white to-slate-50 p-4 dark:from-slate-950 dark:via-slate-900 dark:to-amber-950/30"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-amber-300/40 blur-3xl dark:bg-amber-700/15"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-amber-200/30 blur-3xl dark:bg-amber-800/10"
      />
      <JewelryBackdrop />

      <div className="relative w-full max-w-md">
        <header className="mb-8 text-center">
          <div className="mb-3 inline-flex">
            <BrandMark size="lg" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">J Calculator</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Jewelry estimate calculator
          </p>
        </header>

        <div className="rounded-3xl border border-slate-200 bg-white/80 p-8 shadow-xl backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/80">
          <h2 className="mb-1 text-lg font-semibold">
            {step === 'email' ? 'Sign in' : 'Verify your email'}
          </h2>
          <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
            {step === 'email'
              ? 'We will email you a one-time code. No password needed.'
              : `Enter the 6-digit code sent to ${email}`}
          </p>

          {step === 'email' && (
            <>
              <GoogleSignInButton from={from} />
              <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-wider text-slate-400">
                <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
                or
                <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
              </div>
              <form onSubmit={onSubmitEmail} className="flex flex-col gap-4">
                <Field
                  label="Full name"
                  hint="Used on your first signup"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  type="text"
                  autoComplete="name"
                  placeholder="Rajesh Kumar"
                  maxLength={120}
                />
                <Field
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  required
                />
                <Button
                  type="submit"
                  fullWidth
                  size="lg"
                  pending={requestMutation.isPending}
                  pendingLabel="Sending…"
                >
                  Send login code
                </Button>
              </form>
            </>
          )}

          {autoMode && verifyMutation.isPending ? (
            <div className="flex flex-col items-center gap-3 py-6 text-sm text-slate-500">
              <Spinner label="Signing you in" />
              <p>Signing you in…</p>
            </div>
          ) : null}

          {step === 'code' && !(autoMode && verifyMutation.isPending) && (
            <form onSubmit={onSubmitCode} className="flex flex-col gap-4">
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="font-medium">6-digit code</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- OTP step is the only focusable input on the page; autofocus matches the user's expectation when arriving from email.
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="h-14 rounded-xl border border-slate-300 bg-white px-4 text-center font-mono text-2xl tracking-[0.6em] outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-950 dark:focus:border-slate-100 dark:focus:ring-slate-100/10"
                  placeholder="••••••"
                />
              </label>
              <Button
                type="submit"
                fullWidth
                size="lg"
                disabled={code.length !== 6}
                pending={verifyMutation.isPending}
                pendingLabel="Verifying…"
              >
                Verify and sign in
              </Button>
              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError(null);
                }}
                className="text-sm text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline dark:hover:text-slate-100"
              >
                Use a different email
              </button>
            </form>
          )}

          {error && (
            <p
              role="alert"
              className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-400"
            >
              {error}
            </p>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400 dark:text-slate-500">
          By continuing you agree to our terms. Single device per account.
        </p>
      </div>
    </main>
  );
}

const API_BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '';

function GoogleSignInButton({ from }: { from: string }): JSX.Element {
  const onClick = (): void => {
    // Browser-side absolute URL ensures the OAuth callback bounces us back to
    // the *current* origin (e.g. preview hash, custom domain) rather than a
    // hard-coded canonical, while the server still validates the redirect
    // against its origin allow-list.
    const target = `${window.location.origin}${from}`;
    const url = `${API_BASE}/api/auth/google/start?redirect=${encodeURIComponent(target)}`;
    window.location.href = url;
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-slate-300 bg-white text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
    >
      <GoogleLogo />
      Continue with Google
    </button>
  );
}

function GoogleLogo(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}

function formatOauthError(code: string): string {
  switch (code) {
    case 'google_cancelled':
      return 'Google sign-in was cancelled. You can try again or use email instead.';
    case 'google_exchange_failed':
      return 'Could not complete Google sign-in. Please try again.';
    default:
      return 'Sign-in failed. Please try again.';
  }
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'rate_limited':
        return 'Too many attempts. Try again in a few minutes.';
      case 'wrong_code':
        return 'Wrong code. Check your inbox and try again.';
      case 'expired':
        return 'Code expired. Request a new one.';
      case 'too_many_attempts':
        return 'Too many incorrect attempts. Request a new code.';
      default:
        return err.message;
    }
  }
  return 'Something went wrong. Please try again.';
}
